import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { http } from '@/shared/api/http';
import { env } from '@/shared/config';

/**
 * Backend returns absolute paths like "/tasks-module-back/files/{id}/raw".
 * The axios http client already has `${apiBase}/${apiPrefix}` as baseURL,
 * so we strip the prefix to avoid the double-prefix bug.
 */
export function toApiPath(url: string): string {
  if (!url) {
    return url;
  }
  // Absolute URL → take pathname only.
  let path = url;
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return url;
    }
  }
  const prefix = `/${env.apiPrefix}`;
  if (env.apiPrefix && path.startsWith(prefix + '/')) {
    return path.slice(prefix.length);
  }
  return path;
}

/** Download a file behind the JWT-authed proxy with the given filename. */
export async function downloadAuthedFile(
  url: string,
  name: string
): Promise<void> {
  const res = await http.get(toApiPath(url), { responseType: 'blob' });
  const blob = res.data as Blob;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Browsers keep the download alive after revoke once it has started.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Скачивание файла модуля по его id. Прямая ссылка бэка (downloadUrl) ведёт в
 * хранилище Bitrix — /upload/disk/..., где файл лежит под хэшем без расширения
 * и без Content-Disposition, а атрибут `download` для кросс-доменной ссылки
 * браузер игнорирует. Поэтому тянем байты своим same-origin прокси и сохраняем
 * под настоящим именем.
 */
export function useFileDownload(): {
  download: (id: number | string, name: string) => Promise<void>;
  downloadingId: string | null;
} {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const download = async (id: number | string, name: string) => {
    const key = String(id);
    setDownloadingId(key);
    try {
      await downloadAuthedFile(`/files/${encodeURIComponent(key)}/raw`, name);
    } catch {
      toast.error('Не удалось скачать файл');
    } finally {
      setDownloadingId(null);
    }
  };

  return { download, downloadingId };
}

/**
 * Lazy-fetch a binary file behind the JWT-authed proxy and expose it as a
 * blob URL. Fetch is gated by `enabled` so we don't hammer the server for
 * every voice bubble until the user actually presses play.
 */
export function useAuthedBlobUrl(
  url: string | undefined,
  enabled: boolean
): {
  blobUrl: string | null;
  isLoading: boolean;
  error: Error | null;
} {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const createdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !url || blobUrl) {
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    http
      .get(toApiPath(url), {
        responseType: 'blob',
        signal: controller.signal,
      })
      .then((res) => {
        const blob = res.data as Blob;
        const created = URL.createObjectURL(blob);
        createdRef.current = created;
        setBlobUrl(created);
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e as Error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [url, enabled, blobUrl]);

  // Revoke the object URL on unmount or whenever the source URL changes.
  useEffect(() => {
    return () => {
      if (createdRef.current) {
        URL.revokeObjectURL(createdRef.current);
        createdRef.current = null;
      }
    };
  }, [url]);

  return { blobUrl, isLoading, error };
}
